/**
 * Token storage utility for JWT tokens
 * Stores access and refresh tokens in localStorage
 */

const ACCESS_TOKEN_KEY = "auth_access_token";
const REFRESH_TOKEN_KEY = "auth_refresh_token";

/**
 * Store access token
 */
export function setAccessToken(token: string): void {
  try {
    localStorage.setItem(ACCESS_TOKEN_KEY, token);
  } catch (error) {
    console.error("[token-storage] Failed to set access token:", error);
  }
}

/**
 * Get access token
 */
export function getAccessToken(): string | null {
  try {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  } catch (error) {
    console.error("[token-storage] Failed to get access token:", error);
    return null;
  }
}

/**
 * Store refresh token
 */
export function setRefreshToken(token: string): void {
  try {
    localStorage.setItem(REFRESH_TOKEN_KEY, token);
  } catch (error) {
    console.error("[token-storage] Failed to set refresh token:", error);
  }
}

/**
 * Get refresh token
 */
export function getRefreshToken(): string | null {
  try {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  } catch (error) {
    console.error("[token-storage] Failed to get refresh token:", error);
    return null;
  }
}

/**
 * Store both tokens
 */
export function setTokens(accessToken: string, refreshToken: string): void {
  setAccessToken(accessToken);
  setRefreshToken(refreshToken);
}

/**
 * Clear all tokens
 */
export function clearTokens(): void {
  try {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  } catch (error) {
    console.error("[token-storage] Failed to clear tokens:", error);
  }
}

/**
 * Check if user has tokens (is logged in)
 */
export function hasTokens(): boolean {
  return getAccessToken() !== null && getRefreshToken() !== null;
}

/**
 * Decode JWT token to check expiration (without verification)
 * Returns expiration timestamp in seconds, or null if invalid
 */
export function getTokenExpiration(token: string): number | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    
    const payload = JSON.parse(atob(parts[1]));
    return payload.exp || null;
  } catch (error) {
    console.error("[token-storage] Failed to decode token:", error);
    return null;
  }
}

/**
 * Check if access token is expired or will expire soon
 * @param bufferSeconds - Buffer time in seconds before expiration to consider token expired (default: 60)
 */
export function isAccessTokenExpired(bufferSeconds: number = 60): boolean {
  const token = getAccessToken();
  if (!token) return true;
  
  const exp = getTokenExpiration(token);
  if (!exp) return true;
  
  const now = Math.floor(Date.now() / 1000);
  return exp <= (now + bufferSeconds);
}

